import { createBrowserRouter, Navigate, useSearchParams } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { Loader2 } from 'lucide-react';
import AppLayout from './layouts/AppLayout';
import RespondentLayout from './layouts/RespondentLayout';
import { AUDITOR_FEEDBACK_ENABLED } from './config/featureFlags';

const FormListPage = lazy(() => import('./pages/forms/FormListPage'));
const FormDetailPage = lazy(() => import('./pages/forms/FormDetailPage'));
const FormEditorPage = lazy(() => import('./pages/forms/FormEditorPage'));
const FormSubmitPage = lazy(() => import('./pages/forms/FormSubmitPage'));
const SubmissionsPage = lazy(() => import('./pages/forms/SubmissionsPage'));
const AnalyticsPage = lazy(() => import('./pages/forms/AnalyticsPage'));
const ApiGuidePage = lazy(() => import('./pages/ApiGuidePage'));
const ApiKeysPage = lazy(() => import('./pages/ApiKeysPage'));
const TranslationEditorPage = lazy(() => import('./pages/forms/TranslationEditorPage'));
const AuditorFeedbackTriggersPage = lazy(() => import('./pages/auditor-feedback/AuditorFeedbackTriggersPage'));
const AuditorFeedbackMonitoringPage = lazy(() => import('./pages/auditor-feedback/AuditorFeedbackMonitoringPage'));
const RespondentErrorsPage = lazy(() => import('./pages/auditor-feedback/RespondentErrorsPage'));
const DevFlagsPage = lazy(() => import('./pages/DevFlagsPage'));

function PageLoader() {
  return (
    <div className="flex items-center justify-center py-32">
      <Loader2 className="w-5 h-5 text-ink-tertiary animate-spin" />
    </div>
  );
}

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

/**
 * Dispatcher for `/forms/:formId`. The same path serves two audiences:
 *  - Respondent deep links from email/push (`?linkId=` or legacy `?link=`) → the public
 *    submit page, rendered in the minimal RespondentLayout (no admin FORM_LIST gate).
 *  - Admins (no deep-link param) → the form detail page inside the full AppLayout.
 *
 * Branching on the query param lets the email format `/forms/<id>?linkId=<x>` resolve
 * (HTTP 200, no 404) while keeping the admin detail route intact.
 */
function FormByIdRoute() {
  const [searchParams] = useSearchParams();
  const isRespondentDeepLink = searchParams.has('linkId') || searchParams.has('link');

  if (isRespondentDeepLink) {
    return (
      <RespondentLayout>
        <Lazy><FormSubmitPage /></Lazy>
      </RespondentLayout>
    );
  }

  return (
    <AppLayout>
      <Lazy><FormDetailPage /></Lazy>
    </AppLayout>
  );
}

export const router = createBrowserRouter([
  // Admin routes — full app layout with header, nav, etc.
  {
    path: '/',
    element: <AppLayout />,
    children: [
      { index: true, element: <Navigate to="/forms" replace /> },
      { path: 'forms', element: <Lazy><FormListPage /></Lazy> },
      { path: 'forms/new', element: <Lazy><FormEditorPage /></Lazy> },
      // NOTE: `forms/:formId` is handled by the top-level FormByIdRoute dispatcher
      // below so respondent deep links can bypass the admin AppLayout gate.
      { path: 'forms/:formId/edit', element: <Lazy><FormEditorPage /></Lazy> },
      { path: 'forms/:formId/submissions', element: <Lazy><SubmissionsPage /></Lazy> },
      { path: 'forms/:formId/analytics', element: <Lazy><AnalyticsPage /></Lazy> },
      { path: 'forms/:formId/translations/:locale', element: <Lazy><TranslationEditorPage /></Lazy> },
      { path: 'api-guide', element: <Lazy><ApiGuidePage /></Lazy> },
      { path: 'api-keys', element: <Lazy><ApiKeysPage /></Lazy> },
      // Hidden developer page — reachable via the 5-click easter egg on the
      // "Pulse Library" title (or directly). Always registered.
      { path: 'dev/flags', element: <Lazy><DevFlagsPage /></Lazy> },
      // Auditor Feedback routes are only registered when the feature flag is on.
      // When off, any /auditor-feedback/* URL (e.g. a bookmark) redirects to /forms.
      ...(AUDITOR_FEEDBACK_ENABLED
        ? [
            { path: 'auditor-feedback/triggers', element: <Lazy><AuditorFeedbackTriggersPage /></Lazy> },
            { path: 'auditor-feedback/monitoring', element: <Lazy><AuditorFeedbackMonitoringPage /></Lazy> },
            { path: 'auditor-feedback/respondent-errors', element: <Lazy><RespondentErrorsPage /></Lazy> },
          ]
        : [{ path: 'auditor-feedback/*', element: <Navigate to="/forms" replace /> }]),
    ],
  },
  // `/forms/:formId` — dispatcher: respondent deep link vs. admin detail.
  // Alias for the legacy `/forms/:formId/submit` so email/push links resolve.
  {
    path: '/forms/:formId',
    element: <FormByIdRoute />,
  },
  // Respondent routes — minimal layout, no admin chrome (legacy submit path)
  {
    path: '/forms/:formId/submit',
    element: <RespondentLayout />,
    children: [
      { index: true, element: <Lazy><FormSubmitPage /></Lazy> },
    ],
  },
]);
